/**
 * Referentiel technique et fonctionnel SuiviPlus
 * Version 4.6.0 — Juin 2026
 *
 * Ce fichier contient l'ensemble des regles metier, specs fonctionnelles
 * et regles de gestion de chaque module de l'application.
 *
 * Mis a jour lors de chaque mise en production (cf. CLAUDE.md > Process CHANGELOG).
 */

export const REFERENTIAL_VERSION = '4.6.0';
export const REFERENTIAL_DATE = '2026-06-19';

export const REFERENTIAL_SECTIONS = [
  // ─── STOCKS ──────────────────────────────────────────────────────────
  {
    id: 'stocks',
    title: 'Stocks & Produits',
    icon: 'Package',
    color: 'teal',
    subsections: [
      {
        id: 'stocks-general',
        title: 'Principes generaux',
        content: `## Gestion des stocks

### Champs principaux
- **Stock actuel** : quantite disponible (entier, jamais negatif — plancher a 0)
- **Stock ideal** : niveau optimal souhaite (defaut : 10 unites)
- **Categorie** : classement du produit (accordion repliable dans l'interface)
- **Fournisseur** : lien optionnel vers un fournisseur

### Alertes automatiques
Les alertes sont declenchees **uniquement sur transition d'etat** (pas a chaque mise a jour) :
- **Alerte critique** : stock passe de >0 a 0 (rupture)
- **Alerte stock bas** : stock passe en dessous du stock ideal (sans etre a 0)
- **Exclusion** : les produits avec commande en cours sont exclus du badge d'alerte et affiches dans une section separee "En commande"
- **Notifications push** : envoyees aux utilisateurs inscrits lors des transitions

### Mouvements de stock
Chaque variation de stock cree un **StockMovement** avec :
- Montant (positif ou negatif)
- Nouveau total apres mouvement
- Personne (nom + initiales, max 3 caracteres)
- Horodatage
- Note descriptive (ex: "Reception commande - Fournisseur - Qty x Prix")

**Declencheurs automatiques** :
1. Creation produit avec stock initial > 0
2. Mise a jour manuelle du stock
3. Reception de commande
4. Saisie de prix avec quantite
5. Ouverture de lot de tracabilite (lait, med, eau)`
      },
      {
        id: 'stocks-orders',
        title: 'Commandes',
        content: `## Systeme de commandes

### Cycle de vie
| Statut | Description | Transitions possibles |
|---|---|---|
| \`pending\` | Creee, en attente | → \`partially_received\` / \`received\` / \`cancelled\` |
| \`partially_received\` | Reception partielle | → \`received\` (cloture) / suite reception |
| \`received\` | Entierement recue | Terminal |
| \`cancelled\` | Annulee | Terminal |

### Reception partielle (cumulative)
- Les quantites recues s'accumulent sur plusieurs livraisons
- \`receivedQty\` augmente a chaque reception
- Validation : \`currentReceived + additionalQty <= quantity\`
- Si pas toutes recues → statut \`partially_received\`
- Si toutes recues → statut \`received\`
- Cloture manuelle possible (bouton "Cloturer")

### A chaque reception
1. Increment du stock produit
2. Creation d'un StockMovement
3. Creation d'une entree PriceHistory (uniquement a la 1ere reception d'un article)
4. Mise a jour du \`receivedQty\` de l'article

### Reference de commande
- Mono-structure : pas de reference
- Multi-structures : format \`CMD-{YYYY}-{code}\` partage entre les commandes liees

### Commande multi-structures
- Si les articles proviennent de structures differentes → creation d'une commande par structure
- Resolution cross-structure : recherche du produit equivalent par nom dans la structure cible
- Si produit inexistant dans la cible → **creation automatique** avec les memes attributs`
      },
      {
        id: 'stocks-prices',
        title: 'Prix & Historique',
        content: `## Historique des prix

### Enregistrement
Les prix sont enregistres dans 3 cas :
1. **Saisie manuelle** : ajout de prix avec fournisseur
2. **Reception de commande** : automatique a la 1ere reception d'un article
3. **Creation de produit** : si prix initial + fournisseur fournis

### Comparaison fournisseurs
- Regroupe toutes les entrees par fournisseur
- Affiche le **dernier prix** de chaque fournisseur
- Tri par prix croissant (moins cher en premier)

### Mise a jour fournisseur
Lors d'une saisie de prix, le fournisseur principal du produit est automatiquement mis a jour vers le fournisseur de la saisie.`
      },
      {
        id: 'stocks-files',
        title: 'Fiches produit',
        content: `## Upload de fiches produit

### Regles
- **Formats acceptes** : PDF, JPG, PNG
- **Taille max** : 5 Mo
- **Stockage** : \`/uploads/products/{productId}-{timestamp}.{ext}\`
- **Remplacement** : l'ancien fichier est supprime du disque avant sauvegarde du nouveau

### Interface
- **Indicateur** : icone paperclip bleu a cote du nom si fiche existante
- **Preview** : modal avec iframe (PDF) ou image, bouton telechargement
- **Permissions** : upload/remplacement/suppression reserves aux admins`
      },
      {
        id: 'stocks-multistructures',
        title: 'Multi-structures',
        content: `## Vue multi-structures (Stocks)

### Toggle "Toutes"
- Visible si l'utilisateur a acces a 2+ structures
- Affiche tous les produits groupes par structure puis categorie
- **Lecture seule** en mode multi (pas d'ajout/modification/suppression)

### Suggestion cross-structure
- Lors de la creation d'un produit, recherche dans les autres structures (debounce 300ms, 3+ caracteres)
- Pre-remplissage du formulaire avec categorie, fournisseur, stock ideal
- Correspondance categorie par nom dans la structure cible

### Liste de courses
- Produits ou \`stock actuel < stock ideal\`
- Quantite a commander = \`stock ideal - stock actuel\``
      }
    ]
  },

  // ─── TRACABILITE ─────────────────────────────────────────────────────
  {
    id: 'traceability',
    title: 'Tracabilite',
    icon: 'FlaskConical',
    color: 'blue',
    subsections: [
      {
        id: 'trace-lifecycle',
        title: 'Cycle de vie des lots',
        content: `## Cycle de vie universel (3 modules)

### Statuts
\`sealed\` (scelle) → \`opened\` (ouvert) → \`empty\` (termine)

Le statut \`expired\` n'est pas stocke — il est calcule en temps reel a partir de la date d'expiration.

### Ouverture d'un lot
**Si quantite > 1** :
1. Un **nouveau lot** est cree avec \`status: opened\`, \`quantity: 1\`
2. Le lot original est **decremente** de 1
3. Le lot original reste \`sealed\`

**Si quantite = 1** (derniere unite) :
- Transition directe du lot en \`opened\`
- Pas de creation de nouveau lot

### Fermeture d'un lot (Terminer)
- Passe le statut a \`empty\`
- Disparait de la liste principale (filtre par defaut)

### Declencheurs a l'ouverture
1. Si le lot est lie a un produit en stock → **decrement stock de 1**
2. Creation d'un StockMovement (note: "Ouverture lot {module} {numero}")
3. Calcul de la date d'expiration d'ouverture

### Date d'ouverture modifiable
L'utilisateur peut choisir une date anterieure via un date picker dans le modal d'ouverture.`
      },
      {
        id: 'trace-durations',
        title: 'Durees par module',
        content: `## Durees d'expiration apres ouverture

| Module | Unite | Defaut | Source | Calcul |
|---|---|---|---|---|
| **Lait** | Semaines | 4 sem. | \`MilkBrand.defaultOpenDuration\` | \`openedAt + (duration × 7) jours\` |
| **Medicament** | Jours | 15 j | \`MedProductType.defaultOpenDuration\` | \`openedAt + duration jours\` |
| **Eau** | Heures | 24 h | \`WaterProductType.defaultOpenDuration\` | \`openedAt + duration heures\` |

### Cas special : Medicaments — validite = peremption
Si \`MedProductType.useExpiryAsOpenedValidity = true\` :
- La date d'expiration d'ouverture = date de peremption originale
- La duree d'ouverture est ignoree
- Cas d'usage : certains medicaments dont la validite reste la date de peremption apres ouverture

### Priorite des durees (Med & Eau)
1. Parametre \`openDuration\` explicite dans la requete
2. \`productType.defaultOpenDuration\` du type de produit
3. Valeur par defaut (15 jours med, 24 heures eau)`
      },
      {
        id: 'trace-alerts',
        title: 'Alertes & Rappels',
        content: `## Alertes tracabilite

### Lots ouverts expires
- **Condition** : \`status = 'opened'\` ET \`openedExpiry < maintenant\`
- **Affichage** : banniere orange en haut de page + badge AlertTriangle + fond orange sur la ligne
- **Persistant** jusqu'a fermeture du lot (bouton Terminer)

### Verification de rappel (3 modules)
- **Endpoint** : \`POST /check-recall\`
- Recherche par numero de lot (insensible a la casse)
- Si un lot avec \`isRecalled: true\` est trouve → banniere rouge d'alerte
- Champs : \`isRecalled\`, \`recallDate\`, \`recallReason\`

### Export PDF
- Filtre par periode (date debut/fin)
- Charge TOUS les lots (y compris termines) puis filtre par periode
- Colonnes specifiques par module :
  - Lait : N° Lot, Marque, Produit, Qte, Peremption, Statut, Ouvert le, Exp. ouverture, Ouvert par
  - Medicament : + Duree (jours)
  - Eau : + Duree (heures)
- jsPDF Helvetica ne supporte pas les accents → textes en ASCII uniquement`
      },
      {
        id: 'trace-brands',
        title: 'Marques & Types',
        content: `## Configuration des marques et types

### Lait — Marques (MilkBrand)
- Gestion admin (CRUD) dans Admin > Tracabilites
- Chaque marque a un nom unique et une duree par defaut (en semaines)
- **Obligatoire** a la creation d'un lot lait (\`milkBrandId\` requis)
- La suppression est bloquee si des lots utilisent la marque

### Medicament — Types de produit (MedProductType)
- Champs : nom, duree par defaut (jours), couleur, icone, \`useExpiryAsOpenedValidity\`
- **Optionnel** a la creation d'un lot
- Admin uniquement pour le CRUD

### Eau — Types de produit (WaterProductType)
- Champs : nom, duree par defaut (heures), couleur, icone
- **Optionnel** a la creation d'un lot
- Admin uniquement pour le CRUD`
      },
      {
        id: 'trace-differences',
        title: 'Differences entre modules',
        content: `## Tableau comparatif

| Aspect | Lait | Medicament | Eau |
|--------|------|-----------|-----|
| **Unite duree** | Semaines | Jours | Heures |
| **Defaut** | 4 sem. | 15 j | 24 h |
| **Config** | MilkBrand (separe) | MedProductType | WaterProductType |
| **Obligatoire** | milkBrandId | Non | Non |
| **Validite=Peremption** | Non | Oui (toggle) | Non |
| **Suivi enfant** | Oui (ownerFirstName) | Non | Non |
| **Rappels** | Oui | Oui | Oui |
| **Multi-structures** | Lecture seule | Lecture seule | Lecture seule |
| **Decrement stock** | Oui | Oui | Oui |`
      }
    ]
  },

  // ─── EQUIPEMENTS ─────────────────────────────────────────────────────
  {
    id: 'equipment',
    title: 'Equipements',
    icon: 'Wrench',
    color: 'purple',
    subsections: [
      {
        id: 'equip-general',
        title: 'Gestion des equipements',
        content: `## Module Equipements

### Statuts
- \`active\` : operationnel (defaut)
- \`in_repair\` : en reparation (auto-set lors de la creation d'une reparation)
- \`decommissioned\` : decomissionne (manuel)

### Garantie (calcul automatique)
- **Formule** : \`warrantyExpiry = purchaseDate + warrantyMonths\`
- Recalculee automatiquement si la date d'achat ou la duree change
- Les deux champs doivent etre renseignes (sinon \`warrantyExpiry = null\`)

### Reparations
- La creation d'une reparation met automatiquement l'equipement en \`in_repair\`
- La completion d'une reparation verifie s'il reste des reparations ouvertes :
  - Si 0 reparation ouverte → retour automatique a \`active\`
  - Si des reparations ouvertes restent → reste en \`in_repair\`

### Upload documents
- **Types** : facture (\`invoice\`) et notice (\`manual\`)
- **Formats** : PDF, JPG, PNG — max 5 Mo
- **Stockage** : \`/uploads/equipment/{id}-{fileType}-{timestamp}.{ext}\`
- Remplacement : suppression du fichier precedent automatique

### Securite
- Les champs modifiables en update sont **whitelistes** : name, brand, model, serialNumber, purchaseDate, warrantyMonths, categoryId, location, status, notes
- Pas de spread \`req.body\` direct`
      },
      {
        id: 'equip-categories',
        title: 'Categories',
        content: `## Categories d'equipements

### Regles
- Nom unique (erreur 409 si doublon)
- Suppression bloquee si des equipements sont lies
- Admin uniquement pour le CRUD

### Categories par defaut (seed)
1. Bureautique
2. Cuisine
3. Entretien
4. Exterieur
5. Mobilier
6. Securite`
      }
    ]
  },

  // ─── MAINTENANCE ─────────────────────────────────────────────────────
  {
    id: 'maintenance',
    title: 'Maintenance',
    icon: 'Settings',
    color: 'orange',
    subsections: [
      {
        id: 'maint-general',
        title: 'Interventions',
        content: `## Module Maintenance

### Statuts
- \`completed\` : terminee (defaut)
- \`scheduled\` : planifiee

### Calcul automatique de nextDueDate
- **Formule** : \`nextDueDate = date + type.frequency (en mois)\`
- Calcule **uniquement a la creation** (pas recalcule en update)
- Necessite que le type ait une frequence definie
- En update, \`nextDueDate\` peut etre modifie manuellement

### Signature
- Canvas HTML5 tactile (pas de dependance externe)
- Stocke en base64 data URL dans \`signatureData\`
- Affichee dans le panneau de detail

### Upload documents
- **Types** : facture (\`invoice\`) et compte-rendu (\`report\`)
- **Formats** : PDF, JPG, PNG — max 5 Mo
- **Stockage** : \`/uploads/maintenance/{id}-{fileType}-{timestamp}.{ext}\``
      },
      {
        id: 'maint-types',
        title: 'Types de maintenance',
        content: `## Types d'intervention

### Champs
- Nom (unique), icone, couleur, frequence (mois), description
- Admin uniquement pour le CRUD
- Suppression bloquee si des interventions sont liees

### Types par defaut (seed)
1. Analyse eau
2. Climatisation
3. Controle electrique
4. Controle sanitaire
5. Deratisation
6. Extincteurs
7. Prelevements surface
8. VMC`
      }
    ]
  },

  // ─── CRECHE ──────────────────────────────────────────────────────────
  {
    id: 'creche',
    title: 'Creche',
    icon: 'Baby',
    color: 'pink',
    subsections: [
      {
        id: 'creche-contracts',
        title: 'Enfants & Contrats',
        content: `## Gestion des places

### Statuts de contrat
| Statut | Label | Description |
|---|---|---|
| \`prospect\` | En attente | Premier contact, pas de contrat |
| \`contrat_envoye\` | Contrat envoye | Document envoye a la famille |
| \`contrat_signe\` | Contrat signe | Retour signe par la famille |
| \`valide_meeko\` | Valide Meeko | Saisi dans le logiciel Meeko |
| \`archive\` | Archive | Enfant parti, contrat termine |

### Contrats annuels
- Un contrat par enfant par annee scolaire (\`schoolYear\` : "2025-26")
- Jours de presence : lundi a vendredi (boolean par jour)
- \`actualDays\` : recalcule automatiquement a chaque changement de jours
- Dates : \`contractStart\` et \`contractEnd\` (1er du mois, optionnels)
- **Dates par defaut** : 1er septembre → 31 aout (annee scolaire)
- **Tarif** : lien vers un \`CrecheTariff\` (optionnel)
- Toggle "Afficher archives" pour voir/cacher les contrats termines

### Age calcule
Affiche dans le tableau sous la forme "X ans Y mois" calcule depuis la date de naissance.`
      },
      {
        id: 'creche-tariffs',
        title: 'Tarifs',
        content: `## Tarifs creche

### Structure
- Nom du tarif, nombre de jours (1-5), montant mensuel (EUR)
- Flag \`isPublicEmployer\` (employeur CAF public)
- Validite : \`validFrom\` / \`validUntil\` (permet de garder l'historique des versions)
- Scope : global (\`structureId = null\`) ou specifique a une structure

### Regles
- Lors de la lecture, les tarifs globaux + specifiques a la structure sont combines
- Creation possible pour plusieurs structures a la fois (tableau \`structureIds\`)
- Tri par nombre de jours croissant, puis employeur public`
      },
      {
        id: 'creche-capacity',
        title: 'Capacite & Occupation',
        content: `## Capacite

### Par structure, par jour
- Un enregistrement par structure (unique constraint)
- 5 valeurs : lundi a vendredi (defaut 14 places par jour)
- Modifiable via le bouton Settings dans le pied du tableau

### Occupation
- Compte les contrats actifs par jour de la semaine
- **Filtres d'exclusion** :
  - Statut \`archive\`
  - \`contractEnd\` < aujourd'hui (expire)
  - \`contractStart\` > aujourd'hui (pas encore commence)
- Pour chaque jour, compte les contrats ou ce jour est \`true\`

### Projection CA
- Filtre : contrats avec statut \`contrat_envoye\`, \`contrat_signe\`, ou \`valide_meeko\` + tarif renseigne
- \`CA mensuel\` = somme des montants mensuels des tarifs
- \`CA annuel\` = CA mensuel × 12`
      },
      {
        id: 'creche-documents',
        title: 'Suivi documents familles',
        content: `## Documents familles

### Principe
Suivi des documents administratifs demandes aux familles, par enfant et par annee scolaire.

### Types de documents (admin)
- 16 types par defaut (seedes au demarrage)
- Champs : nom, position (ordre), type de champ (\`checkbox\` ou \`date_validity\`), duree de validite (mois)
- **Flag persistant** :
  - \`persistent = true\` → le check est stocke avec \`schoolYear = 'permanent'\` (ex: carte d'identite — pas besoin de redemander chaque annee)
  - \`persistent = false\` → stocke avec l'annee scolaire courante (ex: certificat medical — a renouveler)

### Checks par enfant
- Contrainte unique : \`[childId, documentTypeId, schoolYear]\`
- Pour les types \`checkbox\` : coche / non coche / N/A
- Pour les types \`date_validity\` : date + calcul automatique de \`validUntil = date + validityMonths mois\`
- Les dates expirees sont signalees visuellement (fond rouge)

### Filtrage des enfants eligibles
Seuls les enfants ayant au moins un contrat avec le statut :
- \`contrat_signe\`
- \`valide_meeko\`
- \`archive\`

Les enfants \`prospect\` et \`contrat_envoye\` sont exclus du tableau.

### Archivage
- Un enfant est considere "archive" si tous ses contrats eligibles sont en statut \`archive\`
- Toggle "Afficher archives" (meme comportement que l'onglet Places)`
      },
      {
        id: 'creche-regularization',
        title: 'Regularisation CMG',
        content: `## Simulateur de regularisation

### Fonctionnement
- 2 modes : enfant existant (pre-rempli) / saisie manuelle
- Calcul automatique avec debounce 500ms
- Comparatif CMG : 2 scenarios (contrat maintenu vs depart anticipe)
- Modele de communication auto-genere, copier-coller direct pour la famille

### Baremes CMG
- CRUD admin avec periode de validite (\`validFrom\` / \`validUntil\`)
- Le simulateur filtre automatiquement les baremes valides selon la date de depart selectionnee
- Reset automatique si le bareme selectionne n'est plus dans la periode

### Historique
- Sauvegarde/rechargement/suppression de simulations
- Liees a l'utilisateur connecte`
      }
    ]
  },

  // ─── RH ──────────────────────────────────────────────────────────────
  {
    id: 'hr',
    title: 'Ressources Humaines',
    icon: 'Users',
    color: 'indigo',
    subsections: [
      {
        id: 'hr-employees',
        title: 'Employes',
        content: `## Gestion des employes

### Acces
**Admin uniquement** sur toutes les routes RH.

### Champs principaux
- Prenom (trim), Nom (trim + MAJUSCULES)
- Type de contrat (CDI, CDD, Stage)
- Heures hebdomadaires (decimal)
- Convention : categorie + echelon (IDCC 3127 petite enfance)
- Dates : embauche, fin, fin periode essai, fin CDD
- Statut : \`active\` (defaut) / \`inactive\`
- Lien optionnel vers une candidature (\`applicationId\`)

### Suppression (RGPD)
Avant suppression, le prenom et nom sont remplaces par "DELETED", email/telephone/notes mis a null.

### Dashboard RH
- Employes actifs, masse salariale, charges totales, CA, ratio masse/CA
- Alerte si ratio > 68%
- CDD expirant dans les 30 jours
- Snapshots mensuels pour graphique tendance`
      },
      {
        id: 'hr-salary',
        title: 'Salaires',
        content: `## Historique des salaires

### Chiffrement
- Le salaire brut mensuel est chiffre en **AES-256-GCM** via la cle \`HR_ENCRYPTION_KEY\`
- Dechiffre uniquement en memoire pour affichage/calcul
- Stocke en hexadecimal dans la base

### Regles
- Salaire doit etre > 0
- Stocke avec 2 decimales
- Plusieurs entrees par employe (historique), triees par date effective decroissante
- Commentaire optionnel (ex: "Augmentation 2026")

### Simulation
- Entree : salaire actuel + montant augmentation
- Sortie : nouveau salaire, % augmentation, impact mensuel et annuel`
      },
      {
        id: 'hr-costs',
        title: 'Charges & CA',
        content: `## Charges patronales

### Categories
\`salaires_bruts\`, \`charges_patronales\`, \`mutuelle\`, \`prevoyance\`, \`formation\`, \`interim\`, \`recrutement\`, \`autres\`

### Chiffrement
Les montants sont chiffres en AES-256-GCM (meme cle que les salaires).

### Upsert
Contrainte unique : \`structureId + month + category\`.
Utilise \`findFirst + create/update\` (pas le upsert Prisma natif).

## Chiffre d'affaires
- Montant decimal (non chiffre)
- Contrainte unique : \`structureId + month\`
- Meme pattern upsert`
      },
      {
        id: 'hr-payroll',
        title: 'Paie & URSSAF',
        content: `## Fiches de paie (Silae)

### Upload
- PDF uniquement, max 20 Mo
- Stockage : \`/uploads/silae-docs/\`

### Auto-detection du nom de fichier
- Pattern : \`NOM_PRENOM_MM_YYYY...pdf\`
- Le systeme detecte automatiquement l'employe et le mois/annee
- Fallback : mois/annee courants si parsing echoue

### Ingestion N8N
- \`POST /api/hr/payroll/silae/ingest\` (Bearer \`SILAE_N8N_TOKEN\`)
- Workflow Google Drive → SuiviPlus

## Declarations URSSAF
- PDF uniquement, max 20 Mo
- Auto-detection mois/annee depuis le nom de fichier
- Montant optionnel
- Meme pattern d'ingestion N8N`
      }
    ]
  },

  // ─── RECRUTEMENT ─────────────────────────────────────────────────────
  {
    id: 'recruitment',
    title: 'Recrutement',
    icon: 'UserPlus',
    color: 'emerald',
    subsections: [
      {
        id: 'recruit-offers',
        title: 'Offres d\'emploi',
        content: `## Offres

### Statuts
\`draft\` → \`published\` → \`closed\` → \`archived\`

### Champs
- Titre (obligatoire), description (obligatoire)
- Type de contrat (CDI, CDD, Stage)
- Salaire, heures, localisation (optionnels)
- Structure (optionnel — null = offre globale)

### Publications multi-plateforme
- Suivi par plateforme : LinkedIn, Indeed, Google Jobs, etc.
- Champs : plateforme, date publication, expiration, URL, reference`
      },
      {
        id: 'recruit-pipeline',
        title: 'Pipeline candidatures',
        content: `## Pipeline de recrutement

### 9 statuts (7 actifs + 2 terminaux)
1. \`nouveau\` — Candidature recue
2. \`preselectionne\` — Premiere selection
3. \`entretien_tel\` — Entretien telephonique
4. \`entretien_physique\` — Entretien en presentiel
5. \`visite_structure\` — Visite de la structure
6. \`offre_envoyee\` — Offre envoyee
7. \`embauche\` — Recrute (cree un Employe)
8. \`refuse\` — Refuse (terminal)
9. \`archive\` — Archive (terminal)

### Regles
- Le statut \`archive\` est exclu par defaut des listes (sauf filtre explicite)
- L'archivage set \`archivedAt = now\`
- L'embauche convertit en Employe : split du nom (1er mot = prenom, reste = nom MAJUSCULES)

### Commentaires
- Multi-utilisateurs (\`authorUserId\` + \`authorName\`)
- Ordonnes par date croissante (fil de discussion)`
      },
      {
        id: 'recruit-interviews',
        title: 'Entretiens',
        content: `## Entretiens structures (3 tours)

| Tour | Type | Description |
|---|---|---|
| 1 | Telephonique | Premier contact, filtrage initial |
| 2 | Presentiel | Entretien approfondi en personne |
| 3 | Visite structure | Mise en situation sur le terrain |

### Templates
- Questions structurees en JSON (types : text, textarea, rating, radio, checkbox)
- Templates par defaut configurables par l'admin
- Les reponses sont stockees en JSON

### Champs
- Date planifiee (\`scheduledAt\`) et date completee (\`completedAt\`)
- Evaluateurs (JSON array de noms)
- Note de 1 a 5, issue (positive/negative/neutre/a discuter)`
      },
      {
        id: 'recruit-cv',
        title: 'Analyse CV & N8N',
        content: `## Analyse CV par IA

### Processus (a la demande, pas automatique)
1. Extraction du texte PDF via \`pdf-parse\`
2. Anonymisation (nom, email, telephone retires)
3. Construction du prompt avec grille d'evaluation + criteres
4. Appel LLM (maxTokens: 3000)
5. Parsing du tableau Markdown pour extraire les scores
6. Stockage de l'evaluation (scores, suggestions, output markdown)

### Grille d'evaluation
- Criteres avec note maximale et guidelines de scoring
- Basee sur la convention IDCC 3127 (petite enfance)
- Opinion finale :
  - >= 85 : Profil d'exception
  - 78-84 : Tres bon profil
  - 70-77 : Bon profil
  - 60-69 : Profil moyen
  - < 60 : Non adapte

### Ingestion N8N (Google Drive)
- \`POST /api/recruitment/cv/ingest\` (Bearer \`RECRUITMENT_N8N_TOKEN\`)
- Cree une entree \`RecruitmentPendingCV\` en attente
- L'admin approuve → cree une candidature
- L'admin rejette → supprime le fichier`
      }
    ]
  },

  // ─── TRAVAUX ─────────────────────────────────────────────────────────
  {
    id: 'workorders',
    title: 'Travaux',
    icon: 'HardHat',
    color: 'amber',
    subsections: [
      {
        id: 'work-general',
        title: 'Bons de travaux',
        content: `## Module Travaux

### Priorites
\`faible\`, \`normale\`, \`haute\`, \`urgente\`

### Statuts
\`nouveau\` → \`planifie\` → \`en_cours\` → \`termine\` / \`annule\`

### Champs
- Categorie, structure, titre, description, localisation
- Signale par, assigne a, entreprise
- Cout estime et cout reel
- Date d'echeance, date d'intervention, date de completion

### Upload documents
- Facture (\`invoice\`) et rapport (\`report\`)
- PDF, JPG, PNG — max 5 Mo
- Stockage : \`/uploads/workorders/\`

### Multi-structures
Mode "Toutes" avec CRUD complet, dropdown structure dans le formulaire.`
      }
    ]
  },

  // ─── TACHES ──────────────────────────────────────────────────────────
  {
    id: 'tasks',
    title: 'Taches',
    icon: 'CheckSquare',
    color: 'cyan',
    subsections: [
      {
        id: 'tasks-general',
        title: 'Gestion des taches',
        content: `## Module Taches

### Priorites
\`faible\`, \`normale\` (defaut), \`haute\`, \`urgente\`

### Statuts
\`a_faire\` (defaut), \`en_cours\`, \`termine\`, \`annule\`

### Transition "termine"
- \`completedAt\` est auto-set lors du passage en \`termine\`
- Efface quand le statut change pour autre chose

### Visibilite
- \`isPrivate = false\` : visible par tous les utilisateurs
- \`isPrivate = true\` : visible uniquement par le createur (\`ownerUserId\`)
- **Filtre systeme** : \`OR [{ isPrivate: false }, { isPrivate: true, ownerUserId }]\`

### Multi-structures (table pivot)
- Table \`TaskStructure\` : une tache peut appartenir a plusieurs structures
- Creation/modification : passage d'un tableau \`structureIds\`
- CRUD complet en mode "Toutes"

### Google Calendar
- Bouton CalendarPlus sur chaque tache avec echeance
- Ouvre Google Calendar avec titre/date/description pre-remplis (lien URL, zero OAuth)

### Vues
- **Liste** : tableau groupe par statut, sections repliables (Termine/Annule replies par defaut), tri par colonnes
- **Kanban** : colonnes par statut, regroupement par structure
- Toggle persisté dans localStorage`
      }
    ]
  },

  // ─── TRESORERIE ──────────────────────────────────────────────────────
  {
    id: 'treasury',
    title: 'Tresorerie',
    icon: 'Landmark',
    color: 'green',
    subsections: [
      {
        id: 'treasury-transactions',
        title: 'Transactions',
        content: `## Flux bancaires

### Acces
**Admin uniquement** sur toutes les routes tresorerie.

### Statuts
| Statut | Description |
|---|---|
| \`to_validate\` | Import brut, a verifier (defaut) |
| \`validated\` | Verifie par l'admin |
| \`reconciled\` | Rapproche avec une facture |

### Compte commun
- \`structureId = null\` represente le **Compte Commun** (partage entre toutes les structures)
- Selecteur propre dans l'interface (independant du selecteur de structure global)
- **Regle de scope** : quand une structure est selectionnee, les transactions du compte commun sont incluses via \`OR [{ structureId: given }, { structureId: null }]\`
- Vue consolidee : somme de toutes les structures + compte commun

### Categorisation en masse
- Checkboxes sur chaque ligne + barre d'actions flottante
- Dropdown categorie + option "Creer regle automatique"
- Creation de \`TreasuryCategoryRule\` pour apprentissage futur (keyword → categorie)`
      },
      {
        id: 'treasury-import',
        title: 'Import CSV & PDF',
        content: `## Import bancaire

### Detection de format
Le systeme detecte automatiquement la banque a partir du contenu :
- **Credit Cooperatif** : contient "Credit Cooperatif", "Libelle", "Debit", "Credit"
- **Boursobank** : contient "dateOp", "accountbalance", ou 9+ colonnes

### Import CSV
1. **Preview** : upload → parsing → suggestions categories → retour pour confirmation
2. **Confirm** : insertion en lot (transaction), creation d'un \`TreasuryImportBatch\`

### Suggestions de categories (17 regles BANK_KEYWORDS)
| Mots-cles | Categorie | Type |
|---|---|---|
| urssaf, cotisation | URSSAF | Depense |
| loyer, bail, foncier | Loyer | Depense |
| edf, engie, electricite | Energie | Depense |
| caf, cnaf, prestation | CAF | Recette |
| meeko | Facturation | Recette |
| ... | ... | ... |

### Import PDF releve
- Upload PDF → extraction texte via \`pdf-parse\`
- Parsing regex (dates DD/MM/YY ou DD/MM/YYYY, montants format francais)
- Detection revenus par mots-cles (virement recu, remboursement, salaire...)
- Meme flow preview/confirm que le CSV`
      },
      {
        id: 'treasury-matching',
        title: 'Rapprochement bancaire',
        content: `## Rapprochement

### Validation
- **Unitaire** : passer une transaction de \`to_validate\` a \`validated\`
- **En lot** : selectionner plusieurs transactions, valider en masse

### Matching factures
- Criteres : montant ± 5%, date ± 30 jours, statut != \`reconciled\`
- Jusqu'a 10 suggestions de transactions matchantes
- Rapprochement : lie la facture a la transaction, passe les deux en \`reconciled\`
- Annulation : detache la facture, repasse la transaction en \`validated\`

### Factures fournisseurs (N8N)
- Workflow Google Drive → \`POST /api/treasury/invoices/ingest\` (Bearer token)
- Cree une \`TreasuryPendingInvoice\` en attente
- Extraction LLM optionnelle (vendeur, montant, date, libelle)
- Admin approuve/rejette/lie a une transaction

### Export iSuite (comptabilite)
- Format CSV : Date, Journal, Piece, Compte, Libelle, Debit, Credit, Statut, Facture
- BOM UTF-8 pour compatibilite Excel
- Montants avec virgule decimale`
      },
      {
        id: 'treasury-budget',
        title: 'Budget & Dashboard',
        content: `## Budget

### Upsert
- Contrainte unique : \`[structureId, categoryId, month, year]\`
- **Ne peut pas utiliser le upsert Prisma** avec \`structureId = null\` dans la contrainte unique
- Pattern : \`findFirst + create/update\`

### Dashboard
- KPIs : recettes, depenses, solde, nombre de transactions
- Graphiques : AreaChart par mois + PieChart par categorie (Recharts)
- Vue annuelle : exercice septembre N → aout N+1
- Soldes mensuels de reference depuis la table \`treasury_balances\`

### Comptes bancaires
- \`TreasuryAccount\` : comptes bancaires PRO
- Seed par defaut : Credit Cooperatif + Boursobank`
      }
    ]
  },

  // ─── FEUILLES DE TEMPS ───────────────────────────────────────────────
  {
    id: 'timesheets',
    title: 'Feuilles de temps',
    icon: 'Clock',
    color: 'violet',
    subsections: [
      {
        id: 'ts-planning',
        title: 'Planning & Saisie',
        content: `## Plannings employes

### Cycle de 4 semaines
- Chaque employe a un planning cyclique sur 4 semaines
- Jours de la semaine (lundi = 1, ..., vendredi = 5)
- Horaires : \`startTime\` / \`endTime\` (format HH:MM)
- Calcul heures : \`(temps - 60 min pause dejeuner) / 60\`

### Generation automatique
- A la creation d'une feuille de temps, les entrees sont pre-remplies depuis le planning
- Pour chaque jour de la periode : determination de la semaine du cycle (\`mod 4\`)
- Les heures prevues deviennent les heures reelles par defaut

### Statuts de validation
\`draft\` → \`submitted\` → \`validated_nicolas\` → \`validated_admin\` → \`sent\`

Chaque etape enregistre un horodatage dedie.

### Types d'absence
| Code | Libelle |
|---|---|
| \`worked\` | Travaille |
| \`cp\` | Conges payes |
| \`sick\` | Maladie |
| \`accident\` | AT |
| \`event\` | Evenement familial |
| \`patho\` | Conge pathologique |
| \`maternity\` | Conge maternite |
| \`rcr\` | RCR |
| \`unpaid\` | Sans solde |`
      },
      {
        id: 'ts-variables',
        title: 'Variables & Cout',
        content: `## Variables de paie

### Heures supplementaires
- HC 10% : heures complementaires a +10%
- HC 25% : heures complementaires a +25%
- HS 25% : heures supplementaires a +25%
- HS 50% : heures supplementaires a +50%
- HN : heures de nuit

### Calcul du cout
- Taux horaire = salaire brut mensuel / 151.67 (base 35h)
- Cout estime = (HC10 × taux × 1.10) + (HC25 × taux × 1.25) + (HS25 × taux × 1.25) + (HS50 × taux × 1.50) + (HN × taux) + extras

### RCR acquis
Heures de recuperation acquises sur la periode.

### Champs extra
2 champs libres (label + valeur) pour compensations additionnelles.

### Export CSV
- UTF-8 avec BOM
- 2 sections : ACTIVITE (par employe/jour) + VARIABLES (par employe)`
      }
    ]
  },

  // ─── COUCHES ─────────────────────────────────────────────────────────
  {
    id: 'diapers',
    title: 'Couches',
    icon: 'Baby',
    color: 'rose',
    subsections: [
      {
        id: 'diapers-dashboard',
        title: 'Dashboard analytique',
        content: `## Module Couches

### Principe
Dashboard analytique **zero saisie** — agregation des donnees existantes :
- Commandes de l'app (Orders)
- Mouvements de stock (StockMovements)
- Contrats creche (CrecheContracts) pour le nombre d'enfants
- Import historique Excel (DiaperMonthlyData)

### Priorite des donnees (regle critique)
1. **Commandes app** priment : si des commandes existent pour un mois, elles sont utilisees
2. **Donnees historiques** (import) : utilisees UNIQUEMENT si aucune commande n'existe pour le mois
3. **Mouvements de stock** : toujours complementaires (ne remplacent jamais les commandes)

### Annee scolaire
- Debut : septembre (configurable)
- 12 mois : septembre N → aout N+1
- Selecteur avec fleches ← → dans l'interface

### 6 KPIs
1. Cout total
2. Nombre de paquets
3. Couches par enfant par jour
4. Stock actuel
5. Couches par mois (moyenne)
6. Mois avant rupture de stock

### Comptage des enfants
- Source : contrats creche actifs (excluant prospect et archive)
- Fallback : valeur par defaut de la configuration (12)

### Donnees historiques
- Table \`DiaperMonthlyData\` avec \`structureId = null\` (donnees communes)
- Import via script depuis Excel
- Gestion par lots d'import (batch), suppression possible par lot`
      },
      {
        id: 'diapers-config',
        title: 'Configuration',
        content: `## Configuration

### Parametres (singleton, id=1)
- \`categoryKeyword\` : mot-cle pour identifier les produits couches (defaut: "couche")
- \`defaultChildrenCount\` : nombre d'enfants par defaut (12)
- \`workingDaysPerMonth\` : jours ouvres pour le calcul par jour (20)
- \`schoolYearStartMonth\` : mois de debut d'annee scolaire (9 = septembre)

### UnitsPerPackage (produits)
- Champ \`unitsPerPackage\` sur chaque produit couche
- **Propagation cross-structure** : la modification met a jour tous les produits du meme nom
- **Fallback regex** : extraction depuis le nom du produit (ex: "Sac de 48 couches T4" → 48)
- Defaut : 1 si aucune detection possible`
      }
    ]
  },

  // ─── DASHBOARD FINANCIER ─────────────────────────────────────────────
  {
    id: 'financial',
    title: 'Dashboard Financier',
    icon: 'TrendingUp',
    color: 'lime',
    subsections: [
      {
        id: 'fin-dashboard',
        title: 'CA vs Charges',
        content: `## Dashboard Financier

### Acces
**Admin uniquement.**

### Principe
Comparaison du Chiffre d'Affaires (factures Meeko importees) vs Charges RH, par structure et par mois.

### Import des factures
- Upload CSV Meeko
- Parsing des colonnes, validation avant import
- Lot identifie par un UUID (\`importBatch\`)
- Chaque lot peut etre supprime (cascade sur les factures)

### Types de facturation
\`inscription\`, \`entreprise\`, \`famille\`

### Statuts de facture
\`payee\`, \`impayee\`, \`en_attente\`

### Affichage
- KPIs globaux
- Graphique CA vs charges par mois
- Tableau par structure`
      }
    ]
  },

  // ─── AGENTS IA ───────────────────────────────────────────────────────
  {
    id: 'agents',
    title: 'Agents IA',
    icon: 'Brain',
    color: 'fuchsia',
    subsections: [
      {
        id: 'agents-overview',
        title: 'Vue d\'ensemble',
        content: `## 5 agents IA + 1 orchestrateur

| Agent | Domaine | Acces | RAG | Memoire |
|---|---|---|---|---|
| **Cygnus** | Support application | Tous users | Oui (docs .md + PDF) | Oui |
| **Orion** | RH + convention ELISFA | Admin only | Oui (docs RH) | Oui |
| **Lyra** | Protocoles creche (HACCP) | Tous users | Oui (docs PDF) | Oui |
| **VEGA** | Proactif (Jarvis) | Admin global | Non | Oui (raw SQL) |
| **Nova** | Orchestrateur routing | Tous users | Non | Non |

### 5 providers LLM supportes
Claude, OpenAI, Mistral, Ollama (local), Gemini

### Auto-apprentissage (memoryLearning.js)
- **learnFromConversation** : extrait max 3 faits reutilisables via LLM apres 4+ messages
- **learnFromAnalysis** : detecte max 2 patterns recurrents dans les insights VEGA
- Deduplication ILIKE avant insertion
- Non-bloquant (\`setImmediate\` apres la reponse)`
      },
      {
        id: 'agents-nova',
        title: 'Nova — Orchestrateur',
        content: `## Nova — Routing rules-based

### Logique de detection
1. Si le module courant est RH ET l'utilisateur est admin → **Orion**
2. Score par mots-cles :
   - VEGA (25+ mots-cles) : alerte, risque, surveillance, anomalie, bilan...
   - RH (20 mots-cles) : salaire, contrat, conge, employe, paie...
   - Lyra (40+ mots-cles) : protocole, haccp, hygiene, securite, medicament...
3. L'agent avec le score le plus eleve est choisi
4. Orion uniquement si l'utilisateur est admin
5. Fallback par defaut → **Cygnus**

### Delegation
Nova appelle directement le controller de l'agent cible (pas de double HTTP).
Chaque reponse inclut un badge "Via Cygnus|Orion|Lyra|VEGA".`
      },
      {
        id: 'agents-vega',
        title: 'VEGA — Agent proactif',
        content: `## VEGA — Monitoring intelligent

### 11 analyseurs CRON
| # | Analyseur | Detection | Severites |
|---|---|---|---|
| 1 | **Stocks** | Stock bas ou en rupture | critical/warning |
| 2 | **Tracabilite** | Lots ouverts expires ou proches | critical/warning |
| 3 | **Maintenance** | Interventions en retard | critical/warning |
| 4 | **Contrats RH** | CDD expirant dans 30j | critical (≤7j) / warning |
| 5 | **Budget** | Variance CA vs charges > seuil | critical/warning |
| 6 | **Tresorerie** | Solde negatif | critical/warning |
| 7 | **Taches** | Taches en retard | critical/warning |
| 8 | **Creche** | Sur/sous-capacite | warning/info |
| 9 | **Qualite donnees** | Donnees manquantes/incoherentes | warning/info |
| 10 | **Couches** | Stock bas, surconsommation | critical/warning/info |
| 11 | **Recrutement** | Offres sans candidats, pipeline bloque | warning/info |

### Seuils configurables (admin)
- \`stock_days_threshold\` : 3 jours
- \`traceability_hours_threshold\` : 24 heures
- \`maintenance_days_threshold\` : 7 jours
- \`hr_contract_days_threshold\` : 30 jours
- \`budget_variance_percent\` : 20%
- \`task_overdue_days_threshold\` : 3 jours
- \`data_quality_enabled\` : toggle on/off

### Enrichissement LLM
- Les insights bruts sont reformules par le LLM avec des recommandations concretes
- Fallback gracieux si le LLM n'est pas configure

### CRON
- Planification : configurable (defaut \`0 */4 * * *\` = toutes les 4 heures)
- Timezone : Europe/Paris
- Execution manuelle possible (bouton "Lancer maintenant" — timeout frontend 120s)

### Widget header
- Bouton Radar avec badge compteur (pulse si critique)
- Panneau slide-in avec 2 onglets : Alertes | Chat`
      },
      {
        id: 'agents-prompt',
        title: 'Architecture des prompts',
        content: `## Construction du prompt systeme

Chaque agent construit un prompt dynamique compose de 7 sections :

1. **Prompt de base** : role et perimetre de l'agent
2. **Instructions de ton** : professionnel / amical / concis / neutre
3. **Contexte personnalise** : champ libre admin
4. **Restrictions** : ce que l'agent ne doit pas faire
5. **Contexte de navigation** : module actuellement consulte
6. **Memoire** : top 10 entrees (faits appris, preferences, corrections)
7. **Documentation RAG** : top 5 chunks pertinents (recherche semantique)

### Cygnus specifique
- Liens de navigation cliquables : \`[→ Nom](/moduleId)\`
- Connaissance de SuiviPlus (fonctionnalites, modules)
- Pas d'acces aux donnees reelles

### VEGA specifique
- Stats des structures en temps reel (produits, equipements, employes, taches)
- Alertes actives injectees
- Contexte live : stocks critiques + taches en retard
- Connaissance des autres agents (redirige si hors scope)`
      }
    ]
  },

  // ─── PERMISSIONS ─────────────────────────────────────────────────────
  {
    id: 'permissions',
    title: 'Permissions & Securite',
    icon: 'Shield',
    color: 'red',
    subsections: [
      {
        id: 'perm-roles',
        title: 'Roles',
        content: `## 4 roles

| Role | Scope | Droits |
|---|---|---|
| \`admin\` | Global | Tout acces, toutes structures, tous modules |
| \`structure_admin\` | Structure | CRUD complet dans sa structure |
| \`staff\` | Structure | Create/update (pas de suppression) |
| \`viewer\` | Structure | Lecture seule |

### Hierarchie
admin > structure_admin > staff > viewer

### Acces structures
- Admin → toutes les structures
- Autres roles → uniquement les structures assignees (table pivot \`user_structures\`)`
      },
      {
        id: 'perm-modules',
        title: 'Modules',
        content: `## Systeme de modules

### Modules disponibles
\`stocks\`, \`traceability\`, \`equipment\`, \`creche\`, \`tasks\`, \`diapers\`

Les modules \`hr\` et \`recruitment\` sont reserves aux admins (pas de checkbox).

### Regles
- Champ \`modules\` sur l'utilisateur : JSON array (ex: \`["stocks","traceability","equipment"]\`)
- \`null\` = **aucun acces** (securite par defaut)
- Admin → tous les modules automatiquement
- La sidebar filtre les items selon les modules de l'utilisateur

### Migration automatique
Au demarrage, les utilisateurs avec \`modules = NULL\` recoivent \`["stocks","traceability","equipment"]\` par defaut.

### Mode Impersonate (DEV uniquement)
- Admin clique "Voir en tant que" dans la gestion utilisateurs
- Simule la vue d'un autre utilisateur (role, modules, structures)
- Banniere amber en haut de page
- Frontend uniquement — le backend garde le vrai role
- Active uniquement si \`REACT_APP_ENABLE_IMPERSONATE=true\` (build arg DEV)`
      },
      {
        id: 'perm-middleware',
        title: 'Middleware',
        content: `## Chaine de middleware

### checkPermission(permission)
Verifie que le role de l'utilisateur a la permission requise.
- 401 si non authentifie
- 403 si permission refusee

### checkStructure()
Verifie que l'utilisateur a acces a la structure demandee.
- Extrait \`structureId\` de query/body/params
- Admin → bypass
- Autres → verifie dans \`user.structures\`

### requireAdmin()
Impose le role admin global.
- 401 si non authentifie
- 403 si role != admin

### hasModuleAccess(user, module)
Verifie que le module est dans le JSON array \`user.modules\`.
- Admin → toujours true
- NULL modules → false
- JSON parse avec fallback securise`
      }
    ]
  },

  // ─── TECHNIQUE ───────────────────────────────────────────────────────
  {
    id: 'technical',
    title: 'Technique',
    icon: 'Server',
    color: 'slate',
    subsections: [
      {
        id: 'tech-auth',
        title: 'Authentification',
        content: `## OAuth2 / OIDC via Authentik

### Flux
1. L'utilisateur clique "Se connecter"
2. Redirection vers Authentik (\`/authorize\`)
3. Authentik verifie les credentials
4. Callback avec code d'autorisation
5. Echange code → tokens (access + refresh)
6. Creation/mise a jour de la session Express

### Sessions
- Stockage : PostgreSQL (TTL 30 jours)
- Cookie : \`connect.sid\` (httpOnly, sameSite: lax)
- Rafraichissement automatique du token`
      },
      {
        id: 'tech-stack',
        title: 'Stack technique',
        content: `## Stack

### Frontend
- React 19 (Create React App)
- Tailwind CSS + CSS Custom Properties (themes)
- Recharts v3 (graphiques)
- jsPDF + autoTable (exports PDF)
- Lucide React (icones)

### Backend
- Node.js / Express
- Prisma ORM + PostgreSQL
- pgvector (extension vectorielle pour RAG futur)
- multer (uploads fichiers)
- Helmet + CORS + rate limiting

### Infrastructure
- Docker (frontend nginx + backend Node + PostgreSQL)
- CI/CD GitHub Actions
- nginx-proxy + Let's Encrypt (SSL)
- Volume Docker pour les uploads`
      },
      {
        id: 'tech-themes',
        title: 'Systeme de themes',
        content: `## 3 themes couleur

| Theme | Couleur primaire | Description |
|---|---|---|
| \`teal\` | Teal/vert | Nature (defaut) |
| \`indigo\` | Indigo/bleu | Moderne |
| \`slate\` | Gris/ardoise | Epure |

### Architecture
- CSS Custom Properties (\`:root\` + \`[data-theme]\`)
- Tailwind etendu : \`bg-primary\`, \`text-primary\`, \`bg-accent\`, etc.
- Persistance : localStorage cle \`suiviplus-theme\`

### Regle
Ne jamais utiliser \`blue-*\` ou \`orange-*\` pour des elements themes.
Utiliser \`primary\` et \`accent\`. Les couleurs semantiques (red, yellow, green) restent fixes.`
      },
      {
        id: 'tech-pwa',
        title: 'PWA & Notifications',
        content: `## Progressive Web App

### Installation
- Installable sur smartphone (manifest.json)
- Service worker pour le cache
- Icones avec \`purpose: "any maskable"\`
- Headers no-cache sur \`index.html\` et \`service-worker.js\` (nginx)

### Notifications push
- Persistantes en PostgreSQL
- Badge dans le header avec compteur
- Alertes temps reel (stock bas, rupture)`
      },
      {
        id: 'tech-multistructures',
        title: 'Multi-structures uniforme',
        content: `## Regle v4.1.0

### Principe
Le mode "Toutes" offre un **CRUD complet** sur tous les modules (jamais read-only).

### Implementation
- Toggle : Bibou | Choupi | Toutes
- Visible si l'utilisateur a 2+ structures
- En mode "Toutes" : dropdown structure dans les formulaires de creation
- Les FABs restent visibles

### Exceptions (reportees)
- Stocks (StockManagementApp trop complexe)
- Commandes
- Phase 3 admin (PeopleManagement, ChildManagement, RoomManagement)`
      }
    ]
  }
];
